export interface AreaWhereInput {
  idIn?: string[]
  nameIn?: string[]
}

export interface AuthorityWhereInput {
  idIn?: string[]
  nameIn?: string[]
}

export interface BusinessWhereInput {
  idIn?: string[]
  nameIn?: string[]
}

export interface Business {
  name?: string
  createdAt?: Date
  businessNumber?: string
  certificateImage?: string
  address?: string
  area?: Area
  branch?: Branch
  isApproved?: boolean
  id?: string
  owner?: User
  jobListings?: JobListing[]
}

export interface CrudFieldOptions<T> {
  items?: T[]
  itemText?: string
  itemValue?: string
}

export interface CrudFormField<T = string> {
  name: string
  label: string
  type: CrudFieldType
  value: T
  required?: boolean
  isDisplayOnly?: boolean
  options?: CrudFieldOptions<T>
  rules?: ((value: T) => boolean | string)[]
  isLoading?: boolean
  isLarge?: boolean
  isDisabled?: () => boolean
  searchFunc?: (searchText: string) => Promise<void>
  getLoading?: () => boolean
  handleValueChanged?: (value: T) => void
  min?: number | Date
  max?: number | Date
  emailNotificationRadius?: number
}

export interface DetailsField<T = object> {
  action?: (item: T) => Promise<unknown>
  href?: (item: T) => string
  isLarge?: boolean
  text: string
  type: DetailsFieldType
  value: string
}

export interface SubFilter<T> {
  text: string
  value: T
  isSelected: boolean
}

export interface Filter<T> {
  action?: (filter: Filter<T>, value?: T) => void | Promise<void>
  subItems: SubFilter<T>[]
  type: FilterType
  field: FilterField
  text: string
  value?: T
}

export interface State {
  areas: Area[]
  authorities: Authority[]
  articles: Article[]
  branches: Branch[]
  configurations: Configuration[]
  jobListings: JobListing[]
  jobListingPlanConfigurations: JobListingPlanConfiguration[]
  jobs: Job[]
  jwt: string
  ruleEngineAlgorithms: RuleEngineAlgorithm[]
  ruleEngineFacts: RuleEngineFact[]
  ruleEngineOperators: RuleEngineOperator[]
  totalAreas: number
  totalAuthorities: number
  totalArticles: number
  totalBranches: number
  totalJobListings: number
  totalJobs: number
  totalUsers: number
  user: User
  users: User[]
}

export interface TextValuePair<T = string> {
  text: string
  value: T
}

export interface User {
  id?: string
  createdAt?: Date
  address?: string
  authority?: Authority
  birthday?: Date
  business?: Business
  closestArea?: Area
  email?: string
  firstName?: string
  hasActiveJobListing?: boolean
  image?: string
  jobs?: Job[]
  isArchived?: boolean
  isConfirmed?: boolean
  lastActivity?: Date
  lastName?: string
  phone?: string
  password?: string
  role?: Role
  mostRecentJobListing?: Date
}

export interface UserJob {
  user: User
  job: Job
}

export interface FetchAreasPayload extends PaginationPayload {
  isArchived?: boolean
  orderBy?: AreaOrderByInput
  basic?: boolean
}

export interface FetchAuthoritiesPayload extends PaginationPayload {
  isArchived?: boolean
  isActive?: boolean
  basic?: boolean
}

export interface PaginationPayload {
  limit?: number
  page?: number
}

export interface FetchUserDataPayload {
  id: string
  jobId?: string
}

export interface FetchUsersPayload extends PaginationPayload {
  businessArea?: AreaWhereInput
  businessAuthority?: AuthorityWhereInput
  email?: string
  searchText?: string
  isArchived: boolean
  isInFocusGroup?: boolean
  isApproved?: boolean | null
  roles?: Role[]
  closestArea?: AreaWhereInput
  authority?: AuthorityWhereInput
  createdAtGte?: string
  lastActivityGte?: string
  ages?: AgeGroups
  lastShiftLogGte?: string
  airedJobListings?: boolean
}

export interface FetchBranchesPayload extends PaginationPayload {
  name?: string
  isArchived?: boolean
}

export interface FetchJobsPayload extends PaginationPayload {
  name?: string
  searchText?: string
  isVerified?: boolean
}

export interface FetchJobListingsPayload extends PaginationPayload {
  searchText?: string
  name?: string
  airsAtGT?: Date
  airsAtLTE?: Date
  area?: AreaWhereInput
  plans?: JobListingPlan[]
  authority?: AuthorityWhereInput
  expiresAtGTE?: Date
  expiresAtLT?: Date
  isBlocked?: boolean
  onlyActive?: boolean
  onlyReported?: boolean
  orderBy?: JobListingsOrderByInput
  business?: string
  approvalStatus?: ApprovalStatus
  status?: JobListingStatus
}

export interface FetchArticlesPayload extends PaginationPayload {
  title?: string
  isArchived?: boolean
  targetAudience?: TargetAudience
}

export interface ArchivedPayload {
  id: string
  isArchived: boolean
}

export interface Branch {
  id?: string
  createdAt?: Date
  image?: string
  isArchived?: boolean
  jobs?: Job[]
  name?: string
}

export interface DataTableAction<T = object> {
  text: string
  icon: string
  handler: (arg: T) => void
}

export interface Job {
  id?: string
  name?: string
  createdAt?: Date
  users?: User[]
  isVerified?: boolean
  address: string
  branch?: Branch
  isArchived: boolean
}

export interface JobApplication {
  id?: string
  createdAt?: Date
  jobListing?: JobListing
  user?: User
  type?: JobApplicationType
}

export interface JobListing extends JobListingTerm {
  id?: string
  airsAt: Date
  status?: JobListingStatus | string
  airsAtGT?: Date
  airsAtLTE?: Date
  allowEmailNotifications: boolean
  area?: Area
  authority?: Authority
  branch?: Branch
  business?: Business
  createdAt?: Date
  description?: string
  employer?: string
  endTime?: string
  expiresAt?: Date
  expiresAtGTE?: Date
  expiresAtLT?: Date
  hourlyRate?: number
  image?: string
  isBlocked?: boolean
  jobsAmount?: number
  jobsMannedAmount?: number
  likesAmount?: number
  logo?: string
  logoImage?: string
  minimumAge?: number
  name?: string
  notes?: string
  phone?: string
  picture?: string
  reports?: JobListingReport[]
  requirements?: string
  salaryType?: SalaryType
  startTime?: string
  weeklyWorkDays?: WeeklyWorkDays[]
  whatsapp?: string
  firstApplicationAt: Date | null
  totalApplications: number
  totalWhatsappApplications: number
  totalPhoneApplications: number
  uniqueApplications: number
  plan: JobListingPlan
  addresses: string[]
  workplace: string
  isAcceptingInAppApplications: boolean
  contactMethods: JobListingContactMethod[]
}

export interface JobListingPlanFeature {
  id: string
  text: string
  toolTipText: string
}

export interface JobListingPlanConfiguration {
  id: string
  plan: JobListingPlan
  title: string
  description: string
  price: number
  publishDuration: number
  applicationAmount: number
  featuresTitle: string
  publishButton: string
  features: JobListingPlanFeature[]
}

export interface JobListingTerm {
  isAccessibleForTheDisabled: boolean
  isBreaks: boolean
  isInShifts: boolean
  isOnSaturday: boolean
  isOvertime: boolean
  isPaidTraining: boolean
  isPublicTransportationAccessible: boolean
  isSalaryRaisable: boolean
  isShuttles: boolean
  isTips: boolean
  isTravelsRefunded: boolean
  isWorkFromHome: boolean
}

export interface JobListingReport {
  createdAt: Date
  id?: string
  text: string
  jobListing: JobListing
  user: User
}

export interface UpdateJobListingPayload {
  id?: string
  isBlocking?: boolean
  jobListing: JobListing
}

export interface UpdateArticlePayload {
  id?: string
  title?: string
  subtitle?: string
  text?: string
  image?: string
  tags?: ArticleTag[]
  isArchived?: boolean
}

export interface UpdateAuthorityPayload {
  id?: string
  isActive?: boolean
  isArchived?: boolean
  leadArticle?: UpdateArticlePayload
  logo?: string
  mayor?: string
  mayorPic?: string
  message?: string
  name?: string
  notes?: string
}

export interface UpdateConfigurationPayload {
  key: string
  value: string
}

export interface NavItem {
  icon?: string
  text: string
  to?: string
  visible(): boolean
  click?(): void
  showSubItems?: boolean
  subItems?: Omit<NavItem, 'subItems'>[]
}

export interface RuleEngineAlgorithm {
  id?: string
  code?: string
  isActive?: boolean
  isCoded?: boolean
  name?: string
  trigger?: RuleEngineAlgorithmTrigger
  rules?: RuleEngineRule[]
}

export interface RuleEngineCondition {
  children?: RuleEngineCondition[]
  compareValue?: string
  fact?: string
  operator?: string
  type: RuleConditionType
}

export interface RuleEngineEvent {
  name: string
  type: RuleEventType
}

export interface RuleEngineFact {
  id: string
  name: string
  type: RuleFactType
  value: (userJob: UserJob) => unknown
}

export interface RuleEngineOperator {
  compareFunction: (factValue: unknown, compareToValue: unknown) => unknown
  id: string
  for: RuleFactType
  name: string
}

export interface RuleEngineRule {
  topCondition: RuleEngineCondition
  eventType: RuleEventType
  eventParam: string
}

export interface RuleEvent {
  type?: RuleEventType
  param?: unknown
}

export interface UpdateJobPayload {
  id: string
  address?: string
  branch?: string
  isArchived?: boolean
  isVerified?: boolean
  name?: string
}

export interface QueryParams {
  areaIds?: string
  authorityIds?: string
  createdAtGte?: string
  lastActivityGte?: string
  lastShiftLogGte?: string
  roles?: string
  isApproved?: 'true' | 'false'
  ages?: string
  plans?: string
  airedJobListings?: 'true' | 'false'
}

export interface Area {
  id?: string
  createdAt?: Date
  activeYouthCount?: number
  approvedBusinessesCount?: number
  availableYouthCount?: number
  emailNotificationRadius?: number
  hourLoggingYouthCount?: number
  isArchived?: boolean
  jobListingsCount?: number
  location?: Location
  name?: string
  new30DaysBusinessesCount?: number
  new30DaysYouthCount?: number
  notes?: string
  relevantBusinessesCount?: number
  relevantYouthCount?: number
  usersCount?: number
  vacantPositionsCount?: number
  lessThanFourteenYouthCount?: number
  fourteenFifteenYouthCount?: number
  sixteenYouthCount?: number
  seventeenYouthCount?: number
  eighteenYouthCount?: number
  nineteenTwentyYouthCount?: number
  twentyOneTwentyFiveYouthCount?: number
  overTwentySixYouthCount?: number
}

export interface Authority {
  id?: string
  activeYouthCount?: number
  approvedBusinessesCount?: number
  availableYouthCount?: number
  createdAt?: Date
  gmapsLocalityId?: string
  hourLoggingYouthCount?: number
  isActive?: boolean
  isArchived?: boolean
  jobListingsCount?: number
  leadArticle?: Article
  logo?: string
  mayor?: string
  mayorPic?: string
  message?: string
  name?: string
  new30DaysBusinessesCount?: number
  new30DaysYouthCount?: number
  notes?: string
  relevantBusinessesCount?: number
  relevantYouthCount?: number
  vacantPositionsCount?: number
}

export interface AuthorityCreateInput {
  searchText: string
}

export interface ArticleCreateInput {
  title?: string
  subtitle?: string
  text?: string
  image?: string
  tags?: ArticleTag[]
}

export interface Article {
  id?: string
  createdAt?: Date
  title?: string
  subtitle?: string
  text?: string
  image?: string
  author?: User
  tags?: ArticleTag[]
  targetAudience?: TargetAudience
  isArchived?: boolean
}

export interface Configuration {
  id: string
  key: string
  value: string
}

export interface FormattedConfiguration extends Configuration {
  hebrewKey: string
}

export interface Location {
  lat: number
  lng: number
}

export interface SortOptions<T> {
  field: keyof T
  direction: SortDirection
}

export interface CreateJobPayload {
  address: string
  name: string
  branch: string
}

export interface FetchJobApplicationPayload {
  jobListingId?: string
}

export enum JobListingPlan {
  BASIC = 'BASIC',
  PRO = 'PRO',
  PREMIUM = 'PREMIUM'
}

export enum JobListingContactMethod {
  PHONE_CALL = 'PHONE_CALL',
  WHATSAPP_MSG = 'WHATSAPP_MSG',
  JOB_APPLICATION = 'JOB_APPLICATION'
}

export enum CrudDialogMode {
  NONE,
  CREATE,
  EDIT,
  DELETE,
  EXPORT_USER_DETAILS,
  ACTIVATE,
  DEACTIVATE,
  VIEW
}

export enum CrudFieldType {
  AUTOCOMPLETE_SELECT,
  DATE,
  DIVIDER,
  IMAGE,
  LOCATION,
  MULTIPLE_SELECT,
  NUMBER,
  SELECT,
  SWITCH,
  TEXT,
  TEXT_AREA,
  TEXT_ARRAY,
  TIME
}

export enum DetailsFieldType {
  LINK,
  TEXT_AREA,
  TEXT,
  SWITCH,
  IMAGE,
  DIVIDER
}

export enum FilterField {
  areaIds = 'areaIds',
  authorityIds = 'authorityIds',
  createdAt = 'createdAt',
  lastActivity = 'lastActivity',
  lastShiftLog = 'lastShiftLog',
  roles = 'roles',
  status = 'status',
  ages = 'ages',
  planTypes = 'planTypes',
  airedJobListings = 'airedJobListings'
}

export enum FilterType {
  CHECKBOX = 'checkbox',
  RADIO = 'radio'
}

export enum Role {
  AUTHENTICATED = 'AUTHENTICATED',
  ADMIN = 'ADMIN',
  EMPLOYER = 'EMPLOYER',
  GUIDE = 'GUIDE'
}

export enum RuleConditionType {
  ANY = 'ANY',
  ALL = 'ALL',
  FACT = 'FACT'
}

export enum RuleEngineAlgorithmTrigger {
  MONTH_END = 'MONTH_END',
  SHIFT_DELETE = 'SHIFT_DELETE',
  SHIFT_END = 'SHIFT_END',
  SHIFT_START = 'SHIFT_START',
  WEEK_END = 'WEEK_END',
  WORK_END = 'WORK_END'
}

export enum RuleEventType {
  ADD_FACT = 'ADD_FACT',
  ADD_SHIFT_INFO = 'ADD_SHIFT_INFO',
  USER_MESSAGE = 'USER_MESSAGE'
}

export enum RuleFactType {
  BOOLEAN = 'BOOLEAN',
  BRANCH = 'BRANCH',
  DATE = 'DATE',
  NUMBER = 'NUMBER',
  STRING = 'STRING',
  TIME = 'TIME'
}

export enum ApprovalStatus {
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED'
}

export enum JobListingStatus {
  ACTIVE = 'ACTIVE',
  PENDING_PAYMENT = 'PENDING_PAYMENT'
}

export enum JobListingExpiration {
  WEEK = 'WEEK',
  TWO_WEEKS = 'TWO_WEEKS',
  MONTH = 'MONTH',
  TWO_MONTH = 'TWO_MONTH',
  THREE_MONTH = 'THREE_MONTH'
}

export enum JobListingsStatus {
  ACTIVE = 'ACTIVE',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  INACTIVE = 'INACTIVE',
  EXPIRED = 'EXPIRED',
  UNAIRED = 'UNAIRED',
  REPORTED = 'REPORTED',
  BLOCKED = 'BLOCKED'
}

export enum WeeklyWorkDays {
  FULLTIME = 'FULLTIME',
  SHIFTS = 'SHIFTS',
  TEMPORARY = 'TEMPORARY',
  ONE = 'ONE',
  TWO_THREE = 'TWO_THREE',
  FOUR_FIVE = 'FOUR_FIVE'
}

export enum SalaryType {
  HOURLY = 'HOURLY',
  MINIMUM = 'MINIMUM',
  TIPPED = 'TIPPED'
}

export enum AlgorithmType {
  RULE_ENGINE,
  CODE
}

export enum ArticleTag {
  SAFETY_IN_WORKSPACE = 'SAFETY_IN_WORKSPACE',
  CONVALESCENCE_FEES = 'CONVALESCENCE_FEES',
  SICK_LEAVE = 'SICK_LEAVE',
  NOTICE_TO_EMPLOYEE = 'NOTICE_TO_EMPLOYEE',
  THE_RIGHT_TO_WORK_IN_SITTING = 'THE_RIGHT_TO_WORK_IN_SITTING',
  COLLECTIVE_AGREEMENTS = 'COLLECTIVE_AGREEMENTS',
  BREAKS = 'BREAKS',
  TRAINING = 'TRAINING',
  CONTRACT_OF_EMPLOYMENT = 'CONTRACT_OF_EMPLOYMENT',
  PROTECTION_OF_YOUTH = 'PROTECTION_OF_YOUTH',
  TIPS = 'TIPS',
  EMPLOYER_EMPLOYEE_RELATIONSHIP = 'EMPLOYER_EMPLOYEE_RELATIONSHIP',
  VACATION_DAYS = 'VACATION_DAYS',
  DEDUCTIONS_AND_FINES = 'DEDUCTIONS_AND_FINES',
  TRAVEL = 'TRAVEL',
  TRANSPORTATION = 'TRANSPORTATION',
  WORK_IN_ELECTION = 'WORK_IN_ELECTION',
  WORK_ON_HOLIDAYS = 'WORK_ON_HOLIDAYS',
  PREGNANCY_AND_BIRTH = 'PREGNANCY_AND_BIRTH',
  EQUALITY_IN_WORK = 'EQUALITY_IN_WORK',
  MINIMUM_WAGE = 'MINIMUM_WAGE',
  MINIMUM_WAGE_FOR_YOUTH = 'MINIMUM_WAGE_FOR_YOUTH',
  OVERTIME = 'OVERTIME',
  WORKING_HOURS_FOR_YOUTH = 'WORKING_HOURS_FOR_YOUTH',
  SELF_RESPONSIBILITY = 'SELF_RESPONSIBILITY',
  NOAL = 'NOAL',
  VICTORIES = 'VICTORIES',
  CURRENT_AFFAIRS = 'CURRENT_AFFAIRS'
}

export enum JobApplicationType {
  PHONE = 'PHONE',
  WHATSAPP = 'WHATSAPP'
}

export enum SortDirection {
  DESC = '_DESC',
  ASC = '_ASC'
}

export enum AreaOrderByInput {
  CREATED_AT_ASC = 'createdAt_ASC',
  CREATED_AT_DESC = 'createdAt_DESC',
  ID_ASC = 'id_ASC',
  ID_DESC = 'id_DESC',
  IS_ARCHIVED_ASC = 'isArchived_ASC',
  IS_ARCHIVED_DESC = 'isArchived_DESC',
  JOB_LISTINGS_COUNT_ASC = 'jobListingsCount_ASC',
  JOB_LISTINGS_COUNT_DESC = 'jobListingsCount_DESC',
  NAME_ASC = 'name_ASC',
  NAME_DESC = 'name_DESC',
  USERS_COUNT_ASC = 'usersCount_ASC',
  USERS_COUNT_DESC = 'usersCount_DESC'
}

export enum JobListingsOrderByInput {
  AIRS_AT_DESC = 'airsAt_DESC'
}

export enum AgeGroups {
  EVERYONE = 'EVERYONE',
  LESS_THAN_FOURTEEN = 'LESS_THAN_FOURTEEN',
  FOURTEEN_FIFTEEN = 'FOURTEEN_FIFTEEN',
  SIXTEEN = 'SIXTEEN',
  SEVENTEEN = 'SEVENTEEN',
  EIGHTEEN = 'EIGHTEEN',
  NINETEEN_TWENTY = 'NINETEEN_TWENTY',
  TWENTY_ONE_TWENTY_FIVE = 'TWENTY_ONE_TWENTY_FIVE',
  OVER_TWENTY_SIX = 'OVER_TWENTY_SIX'
}

export enum TargetAudience {
  YOUTH = 'YOUTH',
  EMPLOYERS = 'EMPLOYERS',
  EVERYONE = 'EVERYONE'
}
